import type React from "react";
import { useState, useEffect, useMemo } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Snackbar,
	Typography,
	CircularProgress,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { Alert } from "@mui/lab";
import { ErrorModal } from "@dexteel/mesf-core";
import { useAssetDetailsContext } from "@/pages/asset-details/context/assetDetailsContext";
import {
	upsertLinkAssetDocument,
	removeDocumentLink,
} from "@/pages/asset-details/repositories/AssetDetailsRepositorie";
import type { AssetDocument } from "@/pages/asset-details/models/AssetDocument";
import { DocumentsCatalogFreshness } from "@/shared/catalogs/components/CatalogFreshnessControls";
import { DocumentsTable } from "./document-table";

interface Props {
	searchAssetDocuments: Function;
	show: boolean;
	isLoading: boolean;
	onHide: (shouldUpdate: boolean) => void;
	AssetId: string | number | null;
}

export const DocumentsPicker: React.FC<Props> = ({
	searchAssetDocuments,
	show,
	onHide,
	AssetId,
	isLoading,
}) => {
	const {
		state: { listAllDocuments, listAssetDocuments },
		actions: { setListAllDocuments },
	} = useAssetDetailsContext();

	const [selectedDocuments, setSelectedDocuments] = useState<AssetDocument[]>(
		[],
	);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [loadingSave, setLoadingSave] = useState(false);

	const currentAssetDocuments = useMemo(() => {
		return listAssetDocuments.filter(
			(doc) => doc.AssetId === AssetId || doc.AssetId === Number(AssetId),
		);
	}, [listAssetDocuments, AssetId]);

	useEffect(() => {
		if (show) {
			setSelectedDocuments(currentAssetDocuments);
		} else {
			setSelectedDocuments([]);
		}
	}, [show, currentAssetDocuments]);

	const handleSelectionChanged = (newSelectedDocuments: AssetDocument[]) => {
		setSelectedDocuments(newSelectedDocuments);
	};

	const handleSave = async () => {
		setLoadingSave(true);
		const documentsToAdd = selectedDocuments.filter(
			(document) =>
				!currentAssetDocuments.some(
					(assetDoc) => assetDoc.DocumentName === document.DocumentName,
				),
		);

		const documentsToRemove = currentAssetDocuments.filter(
			(document) =>
				!selectedDocuments.some(
					(selectedDoc) => selectedDoc.DocumentName === document.DocumentName,
				),
		);

		let needsUpdate = false;

		try {
			if (documentsToAdd.length > 0) {
				for (const doc of documentsToAdd) {
					const upsertResult = await upsertLinkAssetDocument(
						AssetId as number,
						doc.DocumentName,
						"",
					);
					if (!upsertResult.ok) {
						setError(upsertResult.message);
						setLoadingSave(false);
						return;
					}
				}
				needsUpdate = true;
			}

			if (documentsToRemove.length > 0) {
				for (const doc of documentsToRemove) {
					const removeResult = await removeDocumentLink(
						AssetId as number,
						doc.DocumentName,
					);
					if (!removeResult.ok) {
						setError(removeResult.message);
						setLoadingSave(false);
						return;
					}
				}
				needsUpdate = true;
			}

			if (needsUpdate) {
				setSuccessMessage("Documents updated successfully");
				onHide(true);
			} else {
				onHide(false);
			}
		} catch (error: any) {
			setError(error.message || "An error occurred");
		} finally {
			setLoadingSave(false);
		}
	};

	const allDocumentsArray = useMemo(
		() => Object.values(listAllDocuments || {}),
		[listAllDocuments],
	);

	return (
		<>
			<Dialog open={show} onClose={() => onHide(false)} maxWidth="md" fullWidth>
				<DialogTitle sx={{ padding: "24px 24px 0" }}>
					<Typography variant="h5" sx={{ margin: 0, fontWeight: 600 }}>
						DOCUMENTS
					</Typography>
				</DialogTitle>
				<DialogContent>
					<DocumentsTable
						allDocuments={allDocumentsArray}
						assetDocuments={currentAssetDocuments}
						onSelectionChanged={handleSelectionChanged}
						isLoading={isLoading}
					/>
					{/* Same shape useSearchDocuments builds: a map by trimmed name, because the
					    catalog carries trailing spaces on some rows while the links do not. */}
					<DocumentsCatalogFreshness
						onError={setError}
						onReloaded={(rows) => {
							const byName: any = {};
							rows.forEach((row: any) => {
								byName[row.DocumentName?.trim()] = row;
							});
							setListAllDocuments(byName);
						}}
					/>
				</DialogContent>
				<DialogActions
					sx={{ padding: "16px 24px 24px", justifyContent: "flex-end", gap: 2 }}
				>
					<Button
						variant="contained"
						color="inherit"
						onClick={() => onHide(false)}
						sx={{ minWidth: 120 }}
					>
						CANCEL
					</Button>
					<Button
						startIcon={loadingSave && <CircularProgress size="1rem" />}
						disabled={loadingSave}
						variant="contained"
						color="primary"
						onClick={handleSave}
						sx={{ minWidth: 120 }}
					>
						SAVE
					</Button>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={!!successMessage}
				autoHideDuration={6000}
				onClose={() => setSuccessMessage("")}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert onClose={() => setSuccessMessage("")} severity="success">
					{successMessage}
				</Alert>
			</Snackbar>
		</>
	);
};
